<script setup lang="ts">
/**
 * Компонент фильтрации товаров по тегам
 * На мобильных: bottom sheet modal с чекбоксами
 * На десктопе: выпадающий список (Dropdown)
 */
import { computed, ref } from 'vue';
import type { ProductTag } from '~/utils/types/ProductTag';
import { useBottomSheet, useScrollLock } from '~/shared/lib';

const props = defineProps<{
  /** Все доступные теги */
  availableTags: ProductTag[];
  /** Выбранные теги (массив ID) */
  selectedTags: number[];
  /** Размер чипсов */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Состояние загрузки */
  loading?: boolean;
  /** Количество найденных результатов (опционально) */
  resultsCount?: number;
}>();

const emit = defineEmits<{
  /** Событие изменения выбранных тегов */
  'update:selectedTags': [tagIds: number[]];
  /** Событие сброса фильтров */
  'reset': [];
}>();

// Состояние открытия модального окна на мобильных
const isMobileModalOpen = ref(false);

// Bottom sheet логика для свайпа
const {
  sheetRef,
  scrollInnerRef,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
} = useBottomSheet({
  onClose: () => {
    isMobileModalOpen.value = false;
  },
});

// Блокировка скролла страницы при открытии модального окна (с компенсацией
// скроллбара; авто-снятие при размонтировании — см. useScrollLock).
useScrollLock(isMobileModalOpen);

// Проверка, выбран ли тег
const isTagSelected = (tagId: number): boolean => {
  return props.selectedTags.includes(tagId);
};

// Переключение тега
const toggleTag = (tagId: number) => {
  if (props.loading) return;
  
  const currentTags = [...props.selectedTags];
  const index = currentTags.indexOf(tagId);
  
  if (index === -1) {
    currentTags.push(tagId);
  } else {
    currentTags.splice(index, 1);
  }
  
  emit('update:selectedTags', currentTags);
};

// Сброс всех фильтров
const resetFilters = () => {
  emit('update:selectedTags', []);
  emit('reset');
  // Закрываем модалку на мобильных после сброса
  if (isMobileModalOpen.value) {
    isMobileModalOpen.value = false;
  }
  // Закрываем popover на десктопе после сброса
  if (isDesktopPopoverOpen.value) {
    isDesktopPopoverOpen.value = false;
  }
};

// Есть ли активные фильтры
const hasActiveFilters = computed(() => props.selectedTags.length > 0);

// Функция для получения цвета тега
const getTagColor = (tag: ProductTag): string => {
  return tag.color || '#757575';
};

// Функция для получения контрастного цвета текста
const getTextColor = (backgroundColor: string): string => {
  const hex = backgroundColor.replace('#', '');
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

// Преобразуем название иконки
const getIconName = (iconName: string | null): string | undefined => {
  if (!iconName) return undefined;
  if (iconName.startsWith('i-')) return iconName;
  return `i-heroicons-${iconName}`;
};

// Состояние открытия Popover на десктопе
const isDesktopPopoverOpen = ref(false);

// Закрытие модального окна
const closeMobileModal = () => {
  isMobileModalOpen.value = false;
};
</script>

<template>
  <template v-if="availableTags.length > 0">
    <!-- Десктоп: Popover с чекбоксами -->
    <div class="filter-desktop">
      <UPopover
        v-model:open="isDesktopPopoverOpen"
        :popper="{ placement: 'bottom-start' }"
      >
        <UButton
          variant="outline"
          :disabled="loading"
          class="filter-trigger"
        >
          <UIcon name="i-heroicons-funnel" class="w-4 h-4" />
          <span>Фильтр</span>
          <span
            v-if="hasActiveFilters"
            class="filter-badge"
          >
            {{ selectedTags.length }}
          </span>
          <UIcon name="i-heroicons-chevron-down" class="w-4 h-4" />
        </UButton>

        <template #panel>
          <div class="filter-popover-content">
            <!-- Заголовок -->
            <div class="filter-popover-header">
              <span class="filter-popover-title">Фильтры</span>
              <UButton
                v-if="hasActiveFilters"
                color="red"
                variant="ghost"
                size="xs"
                @click="resetFilters"
              >
                <UIcon name="i-heroicons-x-mark" class="w-4 h-4" />
                Сбросить
              </UButton>
            </div>

            <!-- Список тегов с чекбоксами -->
            <div class="filter-popover-list">
              <label
                v-for="tag in availableTags"
                :key="tag.id"
                class="filter-popover-item"
                :class="{ 'filter-popover-item--selected': isTagSelected(tag.id) }"
                :style="isTagSelected(tag.id) ? {
                  '--tag-color': getTagColor(tag),
                  '--tag-text-color': getTextColor(getTagColor(tag)),
                } : {}"
              >
                <UiKitCheckbox
                  :model-value="isTagSelected(tag.id)"
                  :disabled="loading"
                  color="danger"
                  size="sm"
                  @update:model-value="toggleTag(tag.id)"
                />
                <UIcon
                  v-if="tag.icon"
                  :name="getIconName(tag.icon)"
                  class="filter-popover-icon"
                />
                <span class="filter-popover-label">{{ tag.label }}</span>
              </label>
            </div>

            <!-- Счетчик результатов -->
            <div
              v-if="hasActiveFilters && resultsCount !== undefined"
              class="filter-popover-results"
            >
              Найдено: {{ resultsCount }}
            </div>
          </div>
        </template>
      </UPopover>

      <!-- Счетчик результатов на десктопе (вне Popover) -->
      <span
        v-if="hasActiveFilters && resultsCount !== undefined && !isDesktopPopoverOpen"
        class="filter-results-count-desktop"
      >
        Найдено: {{ resultsCount }}
      </span>
    </div>

    <!-- Мобильная версия: Кнопка для открытия bottom sheet -->
    <div class="filter-mobile md:hidden">
      <button
        class="filter-mobile-trigger"
        :disabled="loading"
        type="button"
        @click="isMobileModalOpen = true"
      >
        <UIcon name="i-heroicons-funnel" class="w-5 h-5" />
        <span>Фильтр</span>
        <span
          v-if="hasActiveFilters"
          class="filter-badge"
        >
          {{ selectedTags.length }}
        </span>
      </button>
    </div>

    <!-- Мобильная версия: Bottom Sheet Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div
          v-if="isMobileModalOpen"
          class="filter-modal-overlay"
          @click="closeMobileModal"
        >
          <Transition name="modal-slide">
            <div
              v-if="isMobileModalOpen"
              ref="sheetRef"
              class="filter-modal-content"
              @click.stop
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              <!-- Заголовок с индикатором -->
              <div class="filter-modal-header">
                <!-- Индикатор свайпа -->
                <div class="filter-modal-handle">
                  <div class="filter-modal-handle-bar" />
                </div>
                <div class="filter-modal-header-content">
                  <h3 class="filter-modal-title">Фильтры</h3>
                  <button
                    class="filter-modal-close"
                    type="button"
                    @click="closeMobileModal"
                  >
                    <UIcon name="i-heroicons-x-mark" class="w-6 h-6" />
                  </button>
                </div>
              </div>

              <!-- Список тегов с чекбоксами -->
              <div ref="scrollInnerRef" class="filter-modal-list">
                <label
                  v-for="tag in availableTags"
                  :key="tag.id"
                  class="filter-modal-item"
                  :class="{ 'filter-modal-item--selected': isTagSelected(tag.id) }"
                  :style="isTagSelected(tag.id) ? {
                    '--tag-color': getTagColor(tag),
                    '--tag-text-color': getTextColor(getTagColor(tag)),
                  } : {}"
                >
                  <UiKitCheckbox
                    :model-value="isTagSelected(tag.id)"
                    :disabled="loading"
                    color="danger"
                    size="md"
                    @update:model-value="toggleTag(tag.id)"
                  />
                  <UIcon
                    v-if="tag.icon"
                    :name="getIconName(tag.icon)"
                    class="filter-modal-icon"
                  />
                  <span class="filter-modal-label">{{ tag.label }}</span>
                </label>
              </div>

              <!-- Кнопка сброса -->
              <UiKitButton
                v-if="hasActiveFilters"
                color="danger"
                variant="light"
                size="lg"
                full-width
                class="filter-modal-reset"
                @click="resetFilters"
              >
                <template #leading>
                  <UIcon name="i-heroicons-arrow-path" class="w-5 h-5" />
                </template>
                Сбросить фильтры
              </UiKitButton>

              <!-- Счетчик результатов -->
              <div
                v-if="hasActiveFilters && resultsCount !== undefined"
                class="filter-modal-results"
              >
                Найдено: {{ resultsCount }}
              </div>

              <!-- Кнопка закрытия -->
              <UiKitButton
                color="default"
                size="lg"
                full-width
                @click="closeMobileModal"
              >
                Готово
              </UiKitButton>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </template>
</template>

<style scoped lang="scss">

/* Десктоп версия */
.filter-desktop {
  @apply hidden md:flex items-center gap-3;
}

.filter-trigger {
  @apply relative dark:bg-black border-gray-600 text-black dark:text-white;
}

.filter-badge {
  @apply absolute -top-2 -right-2 flex items-center justify-center 
         w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full;
  animation: popIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.filter-results-count-desktop {
  @apply text-sm text-gray-600 dark:text-gray-400;
  animation: fadeIn 0.3s ease-out;
}

/* Popover контент */
.filter-popover-content {
  @apply w-64 p-2 bg-hui-content1 rounded-hui-md shadow-lg;
}

.filter-popover-header {
  @apply flex items-center justify-between mb-3 px-2 py-1;
}

.filter-popover-title {
  @apply text-sm font-semibold text-hui-foreground;
}

.filter-popover-list {
  @apply flex flex-col gap-1 max-h-96 overflow-y-auto;
}

.filter-popover-item {
  @apply flex items-center gap-2 px-3 py-2 rounded-hui-sm cursor-pointer
         transition-colors
         hover:bg-hui-default-100;

  &--selected {
    background-color: var(--tag-color, rgb(243 244 246));
    color: var(--tag-text-color, rgb(17 24 39));

    .filter-popover-label {
      color: var(--tag-text-color, rgb(17 24 39));
    }
  }
}

.filter-popover-icon {
  @apply w-4 h-4 flex-shrink-0;
}

.filter-popover-label {
  @apply text-sm text-hui-foreground/80 flex-1;
}

.filter-popover-results {
  @apply text-center text-xs text-hui-foreground/60 mt-3 pt-3 border-t border-hui-divider;
}

/* Мобильная версия: Кнопка триггер */
.filter-mobile-trigger {
  @apply flex items-center gap-2 px-4 py-2 
         bg-gray-100 dark:bg-black
         text-black dark:text-gray-300
         rounded-lg font-medium text-sm
         transition-colors
         hover:bg-gray-200 dark:hover:bg-gray-700
         active:scale-95
         disabled:opacity-50 disabled:cursor-not-allowed;
  position: relative;
}

/* Мобильная версия: Bottom Sheet Modal */
.filter-modal-overlay {
  @apply fixed inset-0 flex items-end justify-center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 53;

  @media (min-width: 768px) {
    display: none;
  }
}

.filter-modal-content {
  @apply w-full max-w-full bg-hui-content1 rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto;
  @apply flex flex-col;
}

.filter-modal-header {
  @apply flex flex-col mb-6;
}

.filter-modal-handle {
  @apply flex justify-center mb-4;
}

.filter-modal-handle-bar {
  @apply w-12 h-1 bg-hui-default-300 rounded-full;
}

.filter-modal-header-content {
  @apply flex justify-between items-center;
}

.filter-modal-title {
  @apply text-lg font-semibold text-hui-foreground;
}

.filter-modal-close {
  @apply text-hui-foreground/50 hover:text-hui-foreground
         transition-colors p-1 rounded-full hover:bg-hui-default-100;
}

.filter-modal-list {
  @apply flex flex-col gap-2 mb-4 flex-1 overflow-y-auto;
  max-height: calc(80vh - 200px);
}

.filter-modal-item {
  @apply flex items-center gap-3 px-4 py-3
         rounded-hui-md cursor-pointer
         transition-all duration-200
         bg-hui-content2 border border-hui-divider
         text-hui-foreground
         hover:bg-hui-content3/40;

  &--selected {
    background-color: var(--tag-color);
    color: var(--tag-text-color);
    border-color: var(--tag-color);
  }
}

.filter-modal-icon {
  @apply w-5 h-5 flex-shrink-0;
}

.filter-modal-label {
  @apply text-sm font-medium flex-1;
}

.filter-modal-reset {
  @apply mb-4;
}

.filter-modal-results {
  @apply text-center text-sm text-hui-foreground/60 mb-4;
}

/* Анимации */
@keyframes popIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: transform 0.3s ease-out;
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  transform: translateY(100%);
}
</style>
